import { api } from './authService';

export interface DeploymentServer {
  id?: number;
  tenant_code: string;
  server_address: string;
  ssh_user: string;
  ssh_port?: number;
  ssh_key_path?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AppDeploymentGroup {
  id?: number;
  app_name: string;
  group_name: string;
  description?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  images?: DockerImage[];
}

export interface DockerImage {
  id?: number;
  group_id: number;
  component_name: string;
  image_name: string;
  image_type: 'local' | 'cloud';
  file_path?: string;
  file_size?: number;
  version?: string;
  registry_url?: string;
  registry_project?: string;
  full_image_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DeploymentConfig {
  id?: number;
  tenant_code: string;
  app_name: string;
  group_id?: number;
  config_name: string;
  config_path: string;
  config_content?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DeploymentRecord {
  id?: number;
  tenant_code: string;
  app_name: string;
  group_id: number;
  server_id: number;
  status?: string;
  log_content?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
}

class DeploymentService {
  private baseUrl = '/deployment';

  // 版本组管理
  async getGroups(appName?: string): Promise<AppDeploymentGroup[]> {
    const params = appName ? `?app_name=${appName}` : '';
    const response = await api.get(`${this.baseUrl}/groups${params}`);
    return response.data.data;
  }

  async getGroupDetail(id: number): Promise<AppDeploymentGroup> {
    const response = await api.get(`${this.baseUrl}/groups/${id}`);
    return response.data.data;
  }

  async createGroup(data: Omit<AppDeploymentGroup, 'id' | 'created_at' | 'updated_at'>): Promise<AppDeploymentGroup> {
    const response = await api.post(`${this.baseUrl}/groups`, data);
    return response.data.data;
  }

  async updateGroup(id: number, data: Partial<AppDeploymentGroup>): Promise<void> {
    await api.put(`${this.baseUrl}/groups/${id}`, data);
  }

  async deleteGroup(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/groups/${id}`);
  }

  // 镜像管理
  async getImages(filters?: { groupId?: number; componentName?: string; imageType?: string }): Promise<DockerImage[]> {
    const params = new URLSearchParams();
    if (filters?.groupId) params.append('group_id', filters.groupId.toString());
    if (filters?.componentName) params.append('component_name', filters.componentName);
    if (filters?.imageType) params.append('image_type', filters.imageType);
    
    const queryString = params.toString();
    const response = await api.get(`${this.baseUrl}/images${queryString ? '?' + queryString : ''}`);
    return response.data.data;
  }

  async getImagesByGroup(groupId: number): Promise<DockerImage[]> {
    const response = await api.get(`${this.baseUrl}/images/by-group/${groupId}`);
    return response.data.data;
  }

  async uploadImage(
    groupId: number,
    componentName: string,
    appName: string,
    file: File
  ): Promise<DockerImage> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('group_id', groupId.toString());
    formData.append('component_name', componentName);
    formData.append('app_name', appName);

    const response = await api.post(`${this.baseUrl}/images/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async createCloudImage(data: Omit<DockerImage, 'id' | 'created_at' | 'updated_at'>): Promise<DockerImage> {
    const response = await api.post(`${this.baseUrl}/images`, data);
    return response.data.data;
  }

  async updateImage(id: number, data: Partial<DockerImage>): Promise<void> {
    await api.put(`${this.baseUrl}/images/${id}`, data);
  }

  async deleteImage(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/images/${id}`);
  }

  // 服务器管理
  async getServers(): Promise<DeploymentServer[]> {
    const response = await api.get(`${this.baseUrl}/servers`);
    return response.data.data;
  }

  async createServer(data: Omit<DeploymentServer, 'id' | 'created_at' | 'updated_at'>): Promise<DeploymentServer> {
    const response = await api.post(`${this.baseUrl}/servers`, data);
    return response.data.data;
  }

  async updateServer(id: number, data: Partial<DeploymentServer>): Promise<void> {
    await api.put(`${this.baseUrl}/servers/${id}`, data);
  }

  async deleteServer(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/servers/${id}`);
  }

  // 配置文件管理
  async getConfigs(filters?: { tenantCode?: string; appName?: string; groupId?: number }): Promise<DeploymentConfig[]> {
    const params = new URLSearchParams();
    if (filters?.tenantCode) params.append('tenant_code', filters.tenantCode);
    if (filters?.appName) params.append('app_name', filters.appName);
    if (filters?.groupId !== undefined) params.append('group_id', filters.groupId?.toString() || 'null');
    
    const queryString = params.toString();
    const response = await api.get(`${this.baseUrl}/configs${queryString ? '?' + queryString : ''}`);
    return response.data.data;
  }

  async getConfig(id: number): Promise<DeploymentConfig> {
    const response = await api.get(`${this.baseUrl}/configs/${id}`);
    return response.data.data;
  }

  async getConfigsByGroup(groupId: number): Promise<DeploymentConfig[]> {
    const response = await api.get(`${this.baseUrl}/configs/by-group/${groupId}`);
    return response.data.data;
  }

  async createConfig(data: Omit<DeploymentConfig, 'id' | 'created_at' | 'updated_at'>): Promise<DeploymentConfig> {
    const response = await api.post(`${this.baseUrl}/configs`, data);
    return response.data.data;
  }

  async updateConfig(id: number, data: Partial<DeploymentConfig>): Promise<void> {
    await api.put(`${this.baseUrl}/configs/${id}`, data);
  }

  async deleteConfig(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/configs/${id}`);
  }

  async copyConfig(sourceConfigId: number, targetGroupId: number, newConfigName?: string): Promise<DeploymentConfig> {
    const response = await api.post(`${this.baseUrl}/configs/copy`, {
      source_config_id: sourceConfigId,
      target_group_id: targetGroupId,
      new_config_name: newConfigName
    });
    return response.data.data;
  }

  // 部署管理 - 使用POST请求，返回SSE流
  async deploy(
    tenantCode: string,
    appName: string,
    groupId: number,
    serverId: number,
    configIds: number[],
    onLog: (log: string) => void
  ): Promise<{ recordId: number; success: boolean }> {
    return new Promise((resolve, reject) => {
      // 构建请求体
      const body = JSON.stringify({
        tenant_code: tenantCode,
        app_name: appName,
        group_id: groupId,
        server_id: serverId,
        config_ids: configIds
      });

      // 获取baseURL和headers
      const baseURL = (api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (api.defaults.headers.common) {
        Object.entries(api.defaults.headers.common).forEach(([key, value]) => {
          if (typeof value === 'string') {
            authHeaders[key] = value;
          }
        });
      }

      // 手动附加 token 和租户信息（axios 拦截器不会写入 defaults）
      const token = localStorage.getItem('token');
      if (token) {
        authHeaders.Authorization = `Bearer ${token}`;
      }
      const currentTenant = localStorage.getItem('currentTenant');
      if (currentTenant) {
        authHeaders['X-Tenant-Code'] = currentTenant;
      }

      // 使用fetch进行SSE请求
      fetch(`${baseURL}${this.baseUrl}/deploy`, {
        method: 'POST',
        headers: authHeaders,
        body: body
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('Response body is not readable');
          }

          const readStream = () => {
            reader.read().then(({ done, value }) => {
              if (done) {
                return;
              }

              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.substring(6));
                    if (data.type === 'log') {
                      onLog(data.message);
                    } else if (data.type === 'complete') {
                      resolve({ recordId: data.record_id, success: data.success });
                      return;
                    } else if (data.type === 'error') {
                      reject(new Error(data.message));
                      return;
                    }
                  } catch (e) {
                    // 如果不是JSON，直接作为日志输出
                    onLog(line.substring(6));
                  }
                }
              }

              readStream();
            }).catch(reject);
          };

          readStream();
        })
        .catch(reject);
    });
  }

  async getDeploymentRecords(filters?: {
    tenantCode?: string;
    appName?: string;
    groupId?: number;
    serverId?: number;
  }): Promise<DeploymentRecord[]> {
    const params = new URLSearchParams();
    if (filters?.tenantCode) params.append('tenant_code', filters.tenantCode);
    if (filters?.appName) params.append('app_name', filters.appName);
    if (filters?.groupId) params.append('group_id', filters.groupId.toString());
    if (filters?.serverId) params.append('server_id', filters.serverId.toString());
    
    const queryString = params.toString();
    const response = await api.get(`${this.baseUrl}/records${queryString ? '?' + queryString : ''}`);
    return response.data.data;
  }

  async getDeploymentLog(recordId: number): Promise<string> {
    const response = await api.get(`${this.baseUrl}/records/${recordId}/log`);
    return response.data.data.log;
  }
}

export const deploymentService = new DeploymentService();

